import {
  Block,
  PartialBlock,
  DefaultBlockSchema,
  camelToDataKebab,
} from '@packages/blocknote-core'
import { Note } from '../../../utils/note'
import { TaskItem } from '../../../modules/filter/types'
import dayjs from 'dayjs'

export type ItemType = 'block' | 'task' | 'note' | 'event'

export type ItemVariant = 'search' | 'filter' | 'reference'

export type ItemMetadata = {
  recordName: string
  noteType: number
  lineIndex?: number
  expectedContent?: string
  keyword?: string
  sourceNote?: Note | TaskItem
  scheduledDate?: string
  context?: string // Context information for task state determination (e.g., date context for calendar notes)
}

export type RenderableItem = {
  type: ItemType
  content:
    | string
    | Block<DefaultBlockSchema>
    | PartialBlock<DefaultBlockSchema>
    | Note
    | TaskItem
  metadata: ItemMetadata
}

export type BlockRendererConfig = {
  showTaskToggle: boolean
  showNoteHeader: boolean
  enableKeywordHighlight: boolean
  variant: ItemVariant
}

// Common block type groups for convenience
export const TASK_BLOCK_TYPES = ['taskListItem', 'checkListItem'] as const
export const LIST_BLOCK_TYPES = [
  'bulletListItem',
  'numberedListItem',
  'quoteListItem',
  ...TASK_BLOCK_TYPES,
] as const
export const CONTENT_BLOCK_TYPES = [
  'paragraph',
  'heading',
  'blockQuote',
  'codefence',
  'separator',
] as const

/**
 * Type-safe block type checker that can handle single types, arrays of types, or predefined type groups.
 * This replaces multiple hardcoded type checking patterns throughout the codebase.
 *
 * @param block - The block to check
 * @param types - Single type string, array of types, or readonly array of types
 * @returns true if the block matches any of the specified types
 *
 * @example
 * // Check for single type
 * isBlockType(block, 'heading')
 *
 * // Check for multiple types
 * isBlockType(block, ['taskListItem', 'checkListItem'])
 *
 * // Use predefined type groups
 * isBlockType(block, TASK_BLOCK_TYPES)
 * isBlockType(block, LIST_BLOCK_TYPES)
 */
export function isBlockType(
  block: Block<DefaultBlockSchema> | PartialBlock<DefaultBlockSchema>,
  types: string | readonly string[] | string[]
): boolean {
  if (!block.type) {
    return false
  }
  if (typeof types === 'string') {
    return block.type === types
  }
  return types.includes(block.type)
}

// Type for all block properties that we care about for data attributes
// This matches the properties available in blocknote-core's defaultProps and taskAndCheckProps
export type BlockDataProps = {
  checked: boolean
  cancelled: boolean
  scheduled: boolean
  flagged: number
  folded: boolean
  visible: boolean
}

/**
 * Type-safe property accessor that handles blocknote-core's any-typed props
 */
function getBlockProperty<T>(
  properties: unknown,
  key: string,
  defaultValue: T
): T {
  if (properties && typeof properties === 'object' && key in properties) {
    const value = (properties as Record<string, unknown>)[key]
    return value === undefined ? defaultValue : (value as T)
  }
  return defaultValue
}

/**
 * Gets all block properties with proper defaults and converts them to data attributes.
 * This combines the functionality of getBlockProps and getBlockDataAttributes.
 * This is the single source of truth for block property extraction and data attribute conversion.
 */
export function getBlockDataAttributes(
  block: Block<DefaultBlockSchema> | PartialBlock<DefaultBlockSchema>,
  options?: {
    scheduledDate?: string
    context?: string
  }
): Record<string, boolean | number> {
  // Safely extract properties with proper type handling
  const properties = block.props

  // Determine context-aware scheduled state
  let isScheduled = getBlockProperty(properties, 'scheduled', false)

  // Apply context-aware logic for scheduled tasks
  if (isScheduled && options?.scheduledDate && options?.context) {
    // Use the same logic as getEffectiveTaskState to determine if task should appear scheduled
    const targetDate =
      options.scheduledDate === 'today'
        ? dayjs().format('YYYY-MM-DD') // YYYY-MM-DD format
        : options.scheduledDate

    // Only show as scheduled if NOT in target context (i.e., task appears on different date than scheduled for)
    isScheduled = targetDate !== options.context
  }

  const blockData: BlockDataProps = {
    // Task-specific properties with defaults from taskAndCheckProps
    checked: getBlockProperty(properties, 'checked', false),
    cancelled: getBlockProperty(properties, 'cancelled', false),
    scheduled: isScheduled, // Use context-aware scheduled state
    // Default block properties with defaults from defaultProps
    flagged: getBlockProperty(properties, 'flagged', 0),
    folded: getBlockProperty(properties, 'folded', false),
    visible: getBlockProperty(properties, 'visible', true),
  }

  const dataAttributes: Record<string, boolean | number> = {}

  // Convert each property to a data attribute using BlockNote's naming convention
  for (const [key, value] of Object.entries(blockData)) {
    dataAttributes[camelToDataKebab(key)] = value
  }

  return dataAttributes
}

/**
 * Efficiently gets just the checked property from a block.
 * Use this when you only need to check if a task is completed.
 */
export function isBlockChecked(
  block: Block<DefaultBlockSchema> | PartialBlock<DefaultBlockSchema>
): boolean {
  const properties = block.props
  return getBlockProperty(properties, 'checked', false)
}

/**
 * Efficiently gets just the cancelled property from a block.
 * Use this when you only need to check if a task is cancelled.
 */
export function isBlockCancelled(
  block: Block<DefaultBlockSchema> | PartialBlock<DefaultBlockSchema>
): boolean {
  const properties = block.props
  return getBlockProperty(properties, 'cancelled', false)
}

/**
 * Type guard to check if a block is scheduled
 */
export function isBlockScheduled(
  block: Block<DefaultBlockSchema> | PartialBlock<DefaultBlockSchema>
): boolean {
  const properties = block.props
  return getBlockProperty(properties, 'scheduled', false)
}
